import clsx from 'clsx';
import { useState, useEffect } from 'react';

import {
  IconPhone,
  IconMailOpened,
  IconCalendarEvent,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import CustomModal from '@/components/CustomModal';
import { SchoolLicenseDetailsSkeleton } from '@/components/SkeletonLoader/SchoolLicenseDetailsSkeleton';
import { CommonStatusConfig } from '@/constants/constant';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { getSchoolLicenseDetails } from '@/service/reportService';
import useCommonStore from '@/store/useCommonStore';
import { ISchoolLicenseDetails } from '@/types';

interface ISchoolDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  schoolId: string | null;
}

const SchoolDetailsModal = ({
  setIsOpen,
  schoolId,
}: ISchoolDetailsModalProps) => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();
  const { loadingModal, setLoadingModal } = useCommonStore();
  const [school, setSchool] = useState<ISchoolLicenseDetails | null>(null);
  const statusConfig = school?.status
    ? CommonStatusConfig[school.status]
    : undefined;

  useEffect(() => {
    if (!schoolId) return;

    setSchool(null);

    const fetchDetails = async () => {
      setLoadingModal(true);
      try {
        const response = await getSchoolLicenseDetails(schoolId);
        if (response?.status && response?.data) {
          setSchool(response.data as ISchoolLicenseDetails);
        } else {
          setSchool(null);
        }
      } catch (error) {
        console.error(error);
        setSchool(null);
      } finally {
        setLoadingModal(false);
      }
    };

    fetchDetails();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={formatText('report-analytics.license.school-details.title')}
      position="right"
      className="max-w-150"
      isFooter={false}
    >
      {loadingModal ? (
        <SchoolLicenseDetailsSkeleton />
      ) : (
        <div className="flex flex-col gap-10 w-full h-full">
          {/* Profile Section */}
          <div className="flex flex-col items-center gap-3">
            {school?.school_logo ? (
              <img
                src={school.school_logo}
                alt={school?.school_name || ''}
                width={96}
                height={96}
                className="w-24 h-24 rounded-full border border-neutral-100 object-cover"
              />
            ) : (
              <UserAvatarList
                user={school?.school_name || ''}
                className="w-24 h-24 min-w-24 text-2xl!"
              />
            )}
            <h3 className="text-2xl font-bold text-neutral-primary leading-tight font-mona-sans">
              {school?.school_name || '-'}
            </h3>
            <div className="flex gap-2">
              {statusConfig && (
                <span
                  className={clsx(
                    'px-3 py-1 rounded-full text-xs font-bold font-mona-sans',
                    statusConfig.className
                  )}
                >
                  {formatText(statusConfig.label)}
                </span>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="flex items-start gap-2.5">
              <div className="text-neutral-secondary">
                <IconPhone size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText(
                    'report-analytics.license.school-details.primary-contact'
                  )}
                </span>
                <span className="text-base font-semibold text-neutral-primary font-mona-sans whitespace-nowrap">
                  {school?.primary_contact || '-'}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <div className="text-neutral-secondary">
                <IconMailOpened size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText('common.email')}
                </span>
                <span
                  className="text-base font-semibold text-neutral-primary font-mona-sans truncate max-w-32"
                  title={school?.email || ''}
                >
                  {school?.email || '-'}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <div className="text-neutral-secondary">
                <IconPhone size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText('common.phone-number')}
                </span>
                <span
                  className="text-base font-semibold text-neutral-primary font-mona-sans whitespace-nowrap truncate max-w-32"
                  title={school?.phone || ''}
                >
                  {school?.phone || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* License Overview */}
          <div className="flex flex-col gap-4">
            <h4 className="text-base font-bold text-neutral-primary font-mona-sans">
              {formatText(
                'report-analytics.license.school-details.license-overview'
              )}
            </h4>
            <div className="bg-indigo-light-50 rounded-xl p-4 flex flex-col items-center justify-center gap-1">
              <span className="text-xs font-bold text-brand-default uppercase tracking-wider">
                {formatText(
                  'report-analytics.license.school-details.utilization'
                )}
              </span>
              <span className="text-2xl font-semibold text-neutral-primary font-mona-sans">
                {school?.utilization || 0}%
              </span>
            </div>
          </div>

          {/* Important Dates Box */}
          <div className="bg-brand-light-50 rounded-2xl p-6 flex flex-col gap-6 border border-neutral-light-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-default rounded-xl text-white">
                <IconCalendarEvent size={20} />
              </div>
              <h4 className="text-lg font-bold text-neutral-primary font-mona-sans">
                {formatText(
                  'report-analytics.license.parent-details.important-dates'
                )}
              </h4>
            </div>

            <div className="space-y-4 font-mona-sans">
              <div className="flex items-center justify-between">
                <span className="text-base text-neutral-secondary font-medium">
                  {formatText(
                    'report-analytics.license.parent-details.license-key'
                  )}
                  :
                </span>
                <span className="px-3 py-1 bg-white border-neutral-light-200 rounded-md text-base font-semibold text-neutral-primary">
                  {school?.license_key || '-'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base text-neutral-secondary font-medium">
                  {formatText(
                    'report-analytics.license.school-details.subscription-expiry'
                  )}
                  :
                </span>
                <span className="text-base font-semibold text-neutral-primary">
                  {school?.license_expiry
                    ? formatDate(school.license_expiry)
                    : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base text-neutral-secondary font-medium">
                  {formatText(
                    'report-analytics.license.parent-details.last-login'
                  )}
                  :
                </span>
                <span className="text-base font-semibold text-neutral-primary">
                  {school?.last_login ? formatDate(school.last_login) : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default SchoolDetailsModal;
